import React from 'react';
import SignaturePad from '../SignaturePad';
import type { ReviewStampLive, ReviewStampRow } from '../../api/public';
import {
  STAMP_SIGNER_NAME_MAX_LENGTH,
  signatureLabel,
  signerNameLabel,
  stampRowMaxLength,
} from '../../lib/approval-stamp';

/**
 * The customer's operator approval stamp, filled in on the public review page.
 *
 * Takes the place of the optional name/title card on a ticket whose customer
 * has a stamp. State lives in the page, which owns the approve request and the
 * gate on it; this renders and reports edits.
 *
 * No network call anywhere in here. The signature is the canvas PNG as a data
 * URL and travels in the approve body — `SignatureCapture` uploads through the
 * logged-in path, and this page has no login.
 */
export interface ApprovalStampFormProps {
  stamp: ReviewStampLive;
  values: Record<string, string>;
  onValueChange: (fieldId: string, value: string) => void;
  signerName: string;
  onSignerNameChange: (value: string) => void;
  onSignatureChange: (dataUrl: string | null) => void;
  disabled?: boolean;
}

const inputClass =
  'w-full border rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500';

function RequiredMark() {
  return <span className="text-red-600" aria-hidden="true"> *</span>;
}

function StampRowInput({
  row,
  value,
  onChange,
  disabled,
}: {
  row: ReviewStampRow;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const id = `stamp-${row.field_id}`;
  const common = {
    id,
    value,
    disabled,
    required: row.required,
    'aria-required': row.required || undefined,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      onChange(e.target.value),
    className: inputClass,
  };

  if (row.choices && row.choices.length > 0) {
    // A prefilled value missing from the choices is still offered, or the
    // select would show the first choice while state kept the real one.
    const options = value && !row.choices.includes(value) ? [value, ...row.choices] : row.choices;
    return (
      <select {...common}>
        <option value="">Select…</option>
        {options.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
    );
  }
  if (row.field_type === 'textarea') {
    return <textarea {...common} rows={2} maxLength={stampRowMaxLength(row)} placeholder={row.placeholder ?? undefined} />;
  }
  return (
    <input
      {...common}
      type={row.field_type === 'date' ? 'date' : 'text'}
      maxLength={stampRowMaxLength(row)}
      placeholder={row.placeholder ?? undefined}
    />
  );
}

const ApprovalStampForm: React.FC<ApprovalStampFormProps> = ({
  stamp,
  values,
  onValueChange,
  signerName,
  onSignerNameChange,
  onSignatureChange,
  disabled,
}) => {
  const hasDateRole = stamp.signoff.some((s) => s.role === 'signed_at');
  const dateLabel = stamp.signoff.find((s) => s.role === 'signed_at')?.label;

  return (
    <div className="bg-white rounded-xl shadow-sm border p-5" data-testid="approval-stamp-form">
      <h3
        className={`font-bold mb-3${stamp.operator_color ? '' : ' text-gray-900'}`}
        style={stamp.operator_color ? { color: stamp.operator_color } : undefined}
      >
        {stamp.operator_name}
      </h3>

      <div className="space-y-3">
        {stamp.rows.map((row) => (
          <div key={row.field_id}>
            <label htmlFor={row.read_only ? undefined : `stamp-${row.field_id}`} className="block text-xs font-bold text-gray-700 mb-1">
              {row.label}
              {row.required && !row.read_only && <RequiredMark />}
            </label>
            {row.read_only ? (
              // Bound to the ticket and recomputed by the server at approval —
              // shown, never sent.
              <p className="text-sm text-gray-900" data-testid={`stamp-readonly-${row.field_id}`}>
                {row.value.trim() !== '' ? row.value : '—'}
              </p>
            ) : (
              <StampRowInput
                row={row}
                value={values[row.field_id] ?? ''}
                onChange={(v) => onValueChange(row.field_id, v)}
                disabled={disabled}
              />
            )}
            {row.help_text && <p className="text-xs text-gray-500 mt-1">{row.help_text}</p>}
          </div>
        ))}

        {stamp.requires_signer_name && (
          <div>
            <label htmlFor="stamp-signer-name" className="block text-xs font-bold text-gray-700 mb-1">
              {signerNameLabel(stamp)}
              <RequiredMark />
            </label>
            <input
              id="stamp-signer-name"
              className={inputClass}
              placeholder="e.g. John Smith"
              value={signerName}
              maxLength={STAMP_SIGNER_NAME_MAX_LENGTH}
              required
              aria-required
              disabled={disabled}
              onChange={(e) => onSignerNameChange(e.target.value)}
            />
          </div>
        )}

        {stamp.requires_signature && (
          <SignaturePad
            tone="document"
            label={signatureLabel(stamp)}
            onSave={(dataUrl) => onSignatureChange(dataUrl)}
            onClear={() => onSignatureChange(null)}
          />
        )}

        {hasDateRole && (
          <div>
            <p className="block text-xs font-bold text-gray-700 mb-1">{dateLabel}</p>
            <p className="text-sm text-gray-500">Recorded when you approve</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalStampForm;
